import { useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { walletApi, payApi } from "../utils/api";

const TopUpPage: React.FC = () => {
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  const handlePurchase = async (paymentMethod: "paypal" | "stripe") => {
    const amount = parseFloat(customAmount) || 0;

    if (amount < 1) {
      setError("Minimum amount is $1");
      return;
    }

    setError("");
    setIsProcessing(true);

    try {
      // 创建自定义金额订单
      const orderResponse = await walletApi.createCustomOrder(amount);

      if (orderResponse.code !== 1 || !orderResponse.data) {
        setError(orderResponse.msg || "Failed to create order");
        setIsProcessing(false);
        return;
      }

      const order_sn = (orderResponse.data as { order_sn: string }).order_sn;

      // 调用支付接口
      const payResponse = await (paymentMethod === "paypal"
        ? payApi.paypal(order_sn)
        : payApi.stripe(order_sn));

      if (payResponse.code === 1 && payResponse.data) {
        const payData = payResponse.data as {
          pay_data: Record<string, unknown>;
        };

        // Stripe 返回 checkout session URL
        if (paymentMethod === "stripe") {
          const stripeData = payData.pay_data as { url?: string };
          if (stripeData.url) {
            window.location.href = stripeData.url;
            return;
          }
        }

        // PayPal 返回支付链接
        if (paymentMethod === "paypal") {
          const paypalData = payData.pay_data as {
            links?: Array<{ rel: string; href: string }>;
          };
          if (paypalData.links) {
            const approveLink = paypalData.links.find(
              (link) => link.rel === "approve",
            );
            if (approveLink) {
              window.location.href = approveLink.href;
              return;
            }
          }
        }

        setError("Payment link not found");
      } else {
        setError(payResponse.msg || "Failed to initiate payment");
      }
    } catch {
      setError("Network error, please try again");
    }
    setIsProcessing(false);
  };

  const totalPrice = parseFloat(customAmount) || 0;
  const totalCredits = Math.floor(totalPrice * 100);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Top Up Credits</h1>
        <p className="text-zinc-400 mt-1">
          Add credits to your account balance
        </p>
      </div>

      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Custom Amount */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Custom Amount</h2>
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-zinc-400 text-sm mb-2">
              Amount (USD)
            </label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setError("");
              }}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
              placeholder="Enter amount (min $1)"
            />
          </div>
          <div className="text-zinc-400 text-sm pb-3">
            ={" "}
            <span className="text-white font-medium">
              {totalCredits.toLocaleString()}
            </span>{" "}
            credits
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      {parseFloat(customAmount) > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Payment Summary
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between text-zinc-400">
              <span>Package</span>
              <span className="text-white">Custom Amount</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>Credits</span>
              <span className="text-white">
                {totalCredits.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>Price</span>
              <span className="text-white">${totalPrice.toFixed(2)}</span>
            </div>
            <div className="border-t border-zinc-800 pt-3 flex justify-between">
              <span className="text-white font-medium">Total</span>
              <span className="text-emerald-400 font-bold text-xl">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment Buttons */}
          <div className="mt-6 space-y-3">
            <button
              onClick={() => handlePurchase("stripe")}
              disabled={isProcessing || totalPrice < 1}
              className="w-full py-3 bg-[#635BFF] text-white font-medium rounded-lg hover:bg-[#7A73FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  <span>Pay with Stripe</span>
                </>
              )}
            </button>
          </div>

          <p className="text-zinc-500 text-xs text-center mt-4">
            Secure payment powered by Stripe & PayPal
          </p>
        </div>
      )}
    </div>
  );
};

export default TopUpPage;